import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';

interface FilterItem {
  value: string;
}

interface FilterGroup {
  filterName: string;
  items: FilterItem[];
}

interface GenericFiltersProps {
  filters: FilterGroup[];
  selectedFilters: Record<string, string | string[]>;
  handleOnChange: (name: string, value: string) => void;
  typeCheck?: 'radio' | 'checkbox';
}

interface ISubSection extends FilterGroup {
  selectedFilters: Record<string, string | string[]>;
  onChange: (name: string, value: string) => void;
  typeCheck?: 'radio' | 'checkbox';
}

const labelSx = {
  margin: 0,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  color: 'secondary.main',
  cursor: 'pointer',
  '& .MuiFormControlLabel-label': {
    fontSize: '0.9rem',
    flex: 1,
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },
};

const controlSx = {
  color: 'primary.main',
  '&.Mui-checked': { color: 'primary.main' },
  flexShrink: 0,
};

const SubSection = ({
  items,
  filterName,
  onChange,
  selectedFilters,
  typeCheck = 'checkbox',
}: ISubSection) => {
  const { t } = useTranslation('commonGeneral');

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: '1rem' }}>
        {t(`${filterName}.TITLE`)}
      </Typography>

      <Grid container spacing={1.5} sx={{ maxWidth: '100%' }}>
        {items.map((item) => {
          const isRadio = typeCheck === 'radio';
          const currentValue = selectedFilters[filterName];

          const isChecked = Array.isArray(currentValue)
            ? currentValue.includes(item.value)
            : currentValue === item.value;

          const ControlComponent = isRadio ? Radio : Checkbox;

          return (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.value}>
              <Box
                onClick={() => onChange(filterName, item.value)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  minHeight: '40px',
                  width: '100%',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  cursor: 'pointer',
                  transition: '0.2s',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <FormControlLabel
                  sx={labelSx}
                  label={t(`${filterName}.${item.value}`)}
                  control={<ControlComponent checked={!!isChecked} size="small" sx={controlSx} />}
                />
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

const GenericFilters = ({
  filters,
  selectedFilters,
  handleOnChange,
  typeCheck = 'checkbox',
}: GenericFiltersProps) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2rem', pb: 1 }}>
      {filters.map((filter, index) => (
        <Box key={filter.filterName}>
          <SubSection
            {...filter}
            selectedFilters={selectedFilters}
            onChange={handleOnChange}
            typeCheck={typeCheck}
          />
          {index < filters.length - 1 && <Divider sx={{ mt: 2, opacity: 0.3 }} />}
        </Box>
      ))}
    </Box>
  );
};

export default GenericFilters;
